import React, { useState } from 'react';
import { ArrowLeft, Send, Check, Loader } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const LABELS = [
  { key: 'bug',        text: 'Bug',        color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.3)',   desc: 'Something is broken or not working'       },
  { key: 'suggestion', text: 'Suggestion', color: '#6366f1', bg: 'rgba(99,102,241,0.12)',  border: 'rgba(99,102,241,0.3)',  desc: 'An idea or something we could do better'  },
  { key: 'question',   text: 'Question',   color: '#f59e0b', bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.3)',  desc: 'Anything you want to ask or understand'   },
];

function ContactSection({ onBack }) {
  const { colors } = useTheme();
  const ac = colors.accent;

  const [label,    setLabel]   = useState('');
  const [subject,  setSubject]  = useState('');
  const [message,  setMessage]  = useState('');
  const [loading,  setLoading]  = useState(false);
  const [sent,     setSent]     = useState(false);
  const [error,    setError]    = useState('');

  const canSend = label && subject.trim() && message.trim();

  const handleSend = async () => {
    if (!canSend) { setError('Please select a label, subject, and message'); return; }
    setError(''); setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/contact`,
        { label, subject: subject.trim(), message: message.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send message. Try again.');
    } finally { setLoading(false); }
  };

  const inputBase = {
    background: colors.inputBg || '#0f0f0f',
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    color: colors.primaryText,
    fontSize: 13,
    padding: '10px 12px',
    width: '100%',
    outline: 'none',
  };

  const selectedLabel = LABELS.find(l => l.key === label);

  const placeholders = {
    bug:        'Describe what happened and what you expected instead…',
    suggestion: 'What would you like to see? How would it help you?',
    question:   'Ask away — we read everything…',
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 rounded-md" style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}>
          <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
        </button>
        <h1 className="text-[17px] font-bold" style={{ color: colors.primaryText }}>Contact Us</h1>
      </div>

      {sent ? (
        <div className="rounded-xl p-6 flex flex-col items-center gap-3 text-center"
          style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(22,199,132,0.12)', border: '1px solid rgba(22,199,132,0.3)' }}>
            <Check className="w-6 h-6" style={{ color: '#16C784' }} />
          </div>
          <div>
            <p className="text-[15px] font-semibold" style={{ color: colors.primaryText }}>Message Sent</p>
            <p className="text-[13px] mt-1" style={{ color: colors.secondaryText }}>
              We'll get back to you as soon as possible.
            </p>
          </div>
          <button
            onClick={() => { setSent(false); setLabel(''); setSubject(''); setMessage(''); }}
            className="mt-2 px-4 py-2 rounded-lg text-[13px] font-medium"
            style={{ background: `${ac}15`, color: ac, border: `1px solid ${ac}30` }}
          >
            Send another message
          </button>
        </div>
      ) : (
        <div className="rounded-xl p-4 space-y-4" style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}>
          <p className="text-[13px]" style={{ color: colors.secondaryText }}>
            Something not working? Have an idea? Just want to talk to us? We read everything.
          </p>

          {/* Label chips */}
          <div>
            <p className="text-[11px] mb-2 font-medium uppercase tracking-wide" style={{ color: colors.secondaryText }}>
              What is this about?
            </p>
            <div className="flex gap-2 flex-wrap">
              {LABELS.map(l => {
                const active = label === l.key;
                return (
                  <button
                    key={l.key}
                    onClick={() => { setLabel(l.key); setError(''); }}
                    className="px-3 py-1.5 rounded-full text-[13px] font-semibold transition-all"
                    style={{
                      background:   active ? l.bg    : `${colors.inputBg}`,
                      color:        active ? l.color : colors.secondaryText,
                      border:       `1.5px solid ${active ? l.border : colors.border}`,
                      transform:    active ? 'scale(1.04)' : 'scale(1)',
                    }}
                  >
                    {l.text}
                  </button>
                );
              })}
            </div>
            {selectedLabel && (
              <p className="text-[11px] mt-1.5" style={{ color: selectedLabel.color, opacity: 0.8 }}>
                {selectedLabel.desc}
              </p>
            )}
          </div>

          {/* Subject */}
          <div>
            <p className="text-[11px] mb-1.5 font-medium" style={{ color: colors.secondaryText }}>Subject</p>
            <input
              type="text"
              placeholder="One-line summary"
              value={subject}
              maxLength={100}
              onChange={e => { setSubject(e.target.value); setError(''); }}
              style={inputBase}
            />
          </div>

          {/* Message */}
          <div>
            <p className="text-[11px] mb-1.5 font-medium" style={{ color: colors.secondaryText }}>Message</p>
            <textarea
              placeholder={placeholders[label] || 'Tell us more…'}
              value={message}
              maxLength={2000}
              rows={6}
              onChange={e => { setMessage(e.target.value); setError(''); }}
              style={{ ...inputBase, resize: 'vertical', lineHeight: 1.6 }}
            />
            <p className="text-[11px] text-right mt-1" style={{ color: colors.secondaryText }}>
              {message.length}/2000
            </p>
          </div>

          {error && <p className="text-[12px]" style={{ color: '#ef4444' }}>{error}</p>}

          <button
            onClick={handleSend}
            disabled={loading || !canSend}
            className="w-full py-2.5 rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2"
            style={{
              background: selectedLabel ? selectedLabel.color : ac,
              color: '#fff',
              opacity: (loading || !canSend) ? 0.5 : 1,
            }}
          >
            {loading
              ? <Loader className="w-4 h-4 animate-spin" />
              : <><Send className="w-3.5 h-3.5" /> Send Message</>}
          </button>
        </div>
      )}
    </div>
  );
}

export default ContactSection;
